import React, { useState, useEffect } from 'react';
import '../CSS/App.css';
import { NavScroll } from './NavBar';
import SocialLinks from "./SocialLinks";
import { supabase } from '../utils/supabaseClient';

// Import team logos
import BlueLogo from '../assets/BlueLogo.png';
import GoldLogo from '../assets/GoldLogo.png';
import GreenLogo from '../assets/GreenLogo.png';
import PinkLogo from '../assets/PinkLogo.png';

const TEAM_LOGOS = {
  blue: BlueLogo,
  gold: GoldLogo,
  green: GreenLogo,
  pink: PinkLogo
};

const SQUAD_COLORS = {
  blue: '#3b82f6',
  green: '#10b981',
  pink: '#ec4899',
  gold: '#eab308'
};

export default function Standings() {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Responsive and Mobile Card Expand State
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [expandedTeams, setExpandedTeams] = useState({});
  const [selectedPlayerCard, setSelectedPlayerCard] = useState(null);


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleTeamExpand = (teamName) => {
    setExpandedTeams(prev => {
      const isCurrentlyExpanded = !!prev[teamName];
      return isCurrentlyExpanded ? {} : { [teamName]: true };
    });
  };

  // Highlighted Team State
  const [highlightedTeam, setHighlightedTeam] = useState(null);

  // Player Detail Modal State
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  useEffect(() => {
    async function fetchAllScores() {
      try {
        let allScores = [];
        let from = 0;
        const limit = 1000;
        let hasMore = true;

        while (hasMore) {
          const { data, error } = await supabase
            .from('game_scores_joined')
            .select('*')
            .range(from, from + limit - 1);

          if (error) throw error;

          if (data && data.length > 0) {
            allScores = [...allScores, ...data];
            if (data.length < limit) {
              hasMore = false;
            } else {
              from += limit;
            }
          } else {
            hasMore = false;
          }
        }

        setScores(allScores);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load standings data from Supabase:", err);
        setError("Unable to connect to the Supabase database. Please check configuration.");
        setLoading(false);
      }
    }

    fetchAllScores();
  }, []);

  // Pre-calculate overall events count per player
  const playerOverallEvents = React.useMemo(() => {
    const map = {};
    scores.forEach(row => {
      if (!row.Player) return;
      if (!map[row.Player]) {
        map[row.Player] = new Set();
      }
      map[row.Player].add(row.Event);
    });
    return map;
  }, [scores]);

  // Determine the max event index that actually has games logged
  const maxEvent = React.useMemo(() => {
    if (scores.length === 0) return 0;
    const events = scores.map(s => Number(s.Event)).filter(e => !isNaN(e));
    return events.length > 0 ? Math.max(...events) : 0;
  }, [scores]);

  // Calculate cumulative scores per team for each event
  const chartData = React.useMemo(() => {
    const teamsList = ['Blue', 'Green', 'Gold', 'Pink'];
    
    // Initialize points for events 1 to 7
    const eventPoints = {
      Blue: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
      Green: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
      Gold: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
      Pink: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 }
    };

    // Accumulate raw scores
    scores.forEach(row => {
      const room = row.Room;
      const event = Number(row.Event);
      if (room && eventPoints[room] && event >= 1 && event <= 7) {
        eventPoints[room][event] += row.Score;
      }
    });

    // Compute cumulative points
    const cumulative = {
      Blue: {},
      Green: {},
      Gold: {},
      Pink: {}
    };

    teamsList.forEach(t => {
      let total = 0;
      for (let ev = 1; ev <= 7; ev++) {
        total += eventPoints[t][ev];
        cumulative[t][ev] = total;
      }
    });

    return {
      eventPoints,
      cumulative
    };
  }, [scores]);

  // 1. Header Stat Cards calculation (overall stats)
  // eslint-disable-next-line no-unused-vars
  const stats = React.useMemo(() => {
    if (scores.length === 0) return { totalPlayers: 0, totalEvents: 0, totalGames: 0, topPlayer: '-', topPPGPlayer: '-', topTeam: '-' };

    const playerTotals = {};
    const teamTotals = {};

    scores.forEach(row => {
      // Player
      if (row.Player) {
        if (!playerTotals[row.Player]) {
          playerTotals[row.Player] = { name: row.Player, points: 0, games: 0 };
        }
        playerTotals[row.Player].points += row.Score;
        playerTotals[row.Player].games += 1;
      }
      // Team
      if (row.Room) {
        if (!teamTotals[row.Room]) {
          teamTotals[row.Room] = 0;
        }
        teamTotals[row.Room] += row.Score;
      }
    });

    const playerList = Object.values(playerTotals);
    
    // Top Player
    let topPlayerName = '-';
    let topPlayerPoints = 0;
    playerList.forEach(p => {
      if (p.points > topPlayerPoints) {
        topPlayerPoints = p.points;
        topPlayerName = p.name;
      }
    });

    // Top PPG Leader
    let topPPGName = '-';
    let topPPGVal = 0;
    playerList.forEach(p => {
      const ppg = p.games > 0 ? p.points / p.games : 0;
      if (p.games >= 5 && ppg > topPPGVal) {
        topPPGVal = ppg;
        topPPGName = p.name;
      }
    });

    // Top Team
    let topTeamName = '-';
    let topTeamPoints = 0;
    Object.entries(teamTotals).forEach(([team, pts]) => {
      if (pts > topTeamPoints) {
        topTeamPoints = pts;
        topTeamName = team;
      }
    });

    const totalPlayersCount = Object.keys(playerOverallEvents).length;
    const totalEventsCount = 7;

    return {
      totalPlayers: totalPlayersCount,
      totalEvents: totalEventsCount,
      totalGames: scores.length,
      topPlayer: topPlayerName !== '-' ? `${topPlayerName} (${topPlayerPoints} pts)` : '-',
      topPPGPlayer: topPPGName !== '-' ? `${topPPGName} (${topPPGVal.toFixed(1)} ppg)` : '-',
      topTeam: topTeamName
    };
  }, [scores, playerOverallEvents]);

  // 3b. Mobile Team Standings calculation
  const mobileTeamStandings = React.useMemo(() => {
    // Group scores by Room (representing the team's room score)
    const teamMap = {
      'Blue': { team: 'Blue', totalPoints: 0, eventScores: {}, playerPoints: {} },
      'Green': { team: 'Green', totalPoints: 0, eventScores: {}, playerPoints: {} },
      'Gold': { team: 'Gold', totalPoints: 0, eventScores: {}, playerPoints: {} },
      'Pink': { team: 'Pink', totalPoints: 0, eventScores: {}, playerPoints: {} }
    };

    scores.forEach(row => {
      // Team points are accumulated from the Room played in
      const room = row.Room;
      if (room && teamMap[room]) {
        const tData = teamMap[room];
        tData.totalPoints += row.Score;

        if (!tData.eventScores[row.Event]) {
          tData.eventScores[row.Event] = 0;
        }
        tData.eventScores[row.Event] += row.Score;
      }

      // Track player contributions to their assigned team
      const assignedTeam = row.AssignedTeam;
      if (row.Player && assignedTeam && teamMap[assignedTeam]) {
        const tData = teamMap[assignedTeam];
        if (!tData.playerPoints[row.Player]) {
          tData.playerPoints[row.Player] = 0;
        }
        tData.playerPoints[row.Player] += row.Score;
      }
    });

    const list = Object.values(teamMap).map(t => {
      // Find Best Event
      let bestEvNum = '-';
      let bestEvScore = 0;
      Object.entries(t.eventScores).forEach(([ev, score]) => {
        if (score > bestEvScore) {
          bestEvScore = score;
          bestEvNum = ev;
        }
      });

      // Find Avg Points Per Event
      const uniqueEventsCount = Object.keys(t.eventScores).length || 1;
      const avgPointsPerEvent = t.totalPoints / uniqueEventsCount;

      // Sort players of this team by contribution and slice top 3
      const topPlayers = Object.entries(t.playerPoints)
        .map(([name, points]) => ({ name, points }))
        .sort((a, b) => b.points - a.points)
        .slice(0, 3);

      return {
        team: t.team,
        totalPoints: t.totalPoints,
        bestEvent: bestEvNum !== '-' ? `Event ${bestEvNum} (${bestEvScore} pts)` : '-',
        avgPointsPerEvent: avgPointsPerEvent.toFixed(1),
        topPlayers
      };
    });

    // Rank by total points descending
    list.sort((a, b) => b.totalPoints - a.totalPoints);

    return list;
  }, [scores]);

  // Open Player Card Modal (Sleek Squad-style Card)
  const openPlayerCard = async (playerName) => {
    try {
      const { data, error } = await supabase
        .from('players_joined')
        .select('*')
        .eq('Player', playerName)
        .single();
      
      if (error) throw error;
      setSelectedPlayerCard(data);
    } catch (err) {
      console.error("Failed to load player card from Supabase:", err);
      // Fallback: mock from local scores
      const playerScores = scores.filter(x => x.Player === playerName);
      const team = playerScores.length > 0 ? (playerScores[0].AssignedTeam || 'Unassigned') : 'Unassigned';
      let totalPoints = 0;
      const eventsSet = new Set();
      playerScores.forEach(s => {
        totalPoints += s.Score;
        eventsSet.add(s.Event);
      });
      setSelectedPlayerCard({
        Player: playerName,
        Team: team,
        Rank: null,
        "Events Played": eventsSet.size,
        "Games Played": playerScores.length,
        "Game Points": totalPoints,
        "Bonus Points": 0,
        "Overall Points": totalPoints,
        "Pts/Game": playerScores.length > 0 ? totalPoints / playerScores.length : 0,
        Captain: 0
      });
    }
  };

  // Open Player Detail
  // eslint-disable-next-line no-unused-vars
  const openPlayerDetails = (playerName) => {
    const playerScores = scores.filter(x => x.Player === playerName);
    if (playerScores.length === 0) return;

    const team = playerScores[0].AssignedTeam || 'Unassigned';
    
    // Calculate stats specifically for this player
    let totalPoints = 0;
    const eventsSet = new Set();
    const halvesSet = new Set();
    const eventScores = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
    const eventGames = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
    const eventRooms = { 1: new Set(), 2: new Set(), 3: new Set(), 4: new Set(), 5: new Set(), 6: new Set(), 7: new Set() };
    
    let firstHalfPoints = 0;
    let secondHalfPoints = 0;
    const roomCounts = { Blue: 0, Green: 0, Gold: 0, Pink: 0 };

    playerScores.forEach(row => {
      totalPoints += row.Score;
      eventsSet.add(row.Event);
      halvesSet.add(`${row.Event}-${row.Half}`);
      
      eventScores[row.Event] += row.Score;
      eventGames[row.Event] += 1;
      if (row.Room) {
        eventRooms[row.Event].add(row.Room);
        if (roomCounts[row.Room] !== undefined) {
          roomCounts[row.Room] += 1;
        }
      }

      if (row.Half === '1st Half') {
        firstHalfPoints += row.Score;
      } else if (row.Half === '2nd Half') {
        secondHalfPoints += row.Score;
      }
    });

    const gamesPlayed = playerScores.length;
    const eventsPlayed = eventsSet.size;
    const ppg = gamesPlayed > 0 ? totalPoints / gamesPlayed : 0;

    // Find Best Event
    let bestEvNum = '-';
    let bestEvScore = 0;
    Object.entries(eventScores).forEach(([ev, score]) => {
      if (score > bestEvScore) {
        bestEvScore = score;
        bestEvNum = ev;
      }
    });

    // Find Last Event
    let lastEvNum = Math.max(...Array.from(eventsSet));
    let lastEvScore = eventScores[lastEvNum] || 0;

    // Find overall rank of this player
    // Calculate overall player standings (unfiltered) to get correct true rank
    const overallList = calculateTrueOverallStandings(scores);
    const overallRank = overallList.findIndex(x => x.player === playerName) + 1;

    setSelectedPlayer({
      name: playerName,
      team,
      overallRank,
      totalPoints,
      eventsPlayed,
      gamesPlayed,
      ppg,
      bestEvent: bestEvNum !== '-' ? `Event ${bestEvNum} (${bestEvScore} pts)` : '-',
      lastEvent: lastEvNum > 0 ? `Event ${lastEvNum} (${lastEvScore} pts)` : '-',
      firstHalfPoints,
      secondHalfPoints,
      roomCounts,
      eventBreakdown: Object.keys(eventScores).map(ev => ({
        event: Number(ev),
        points: eventScores[ev],
        games: eventGames[ev],
        ppg: eventGames[ev] > 0 ? eventScores[ev] / eventGames[ev] : 0,
        rooms: Array.from(eventRooms[ev]).join(', ')
      })).filter(eb => eb.games > 0)
    });
  };

  // Helper to calculate true season overall rank (unfiltered by search/events)
  const calculateTrueOverallStandings = (allScores) => {
    const map = {};
    allScores.forEach(row => {
      const p = row.Player;
      if (!p) return;
      if (!map[p]) map[p] = { player: p, points: 0 };
      map[p].points += row.Score;
    });
    return Object.values(map).sort((a, b) => b.points - a.points);
  };

  return (
    <div className='background-component'>
      <NavScroll />

      <div className="standings-dashboard">
        <h1 className="squads-title" style={{ marginBottom: '1rem' }}>Season 03 Standings</h1>

        {loading ? (
          <div className="roster-loading">Loading standings dashboard...</div>
        ) : error ? (
          <div className="roster-error">{error}</div>
        ) : isMobile ? (
          <div className="mobile-standings-container">
            {mobileTeamStandings.map((t, idx) => {
              const teamLower = t.team.toLowerCase();
              const logo = TEAM_LOGOS[teamLower];
              const color = SQUAD_COLORS[teamLower];
              const isExpanded = !!expandedTeams[t.team];

              return (
                <div 
                  key={t.team} 
                  className={`mobile-team-card ${isExpanded ? 'expanded' : ''}`}
                  style={{ borderLeft: `6px solid ${color}` }}
                >
                  {/* Card Header (clickable to expand) */}
                  <div 
                    className="mobile-team-card-header" 
                    onClick={() => toggleTeamExpand(t.team)}
                  >
                    <div className="mobile-team-rank">{idx + 1}</div>
                    {logo && (
                      <div className="mobile-team-logo-container">
                        <img 
                          src={logo} 
                          alt={`${t.team} logo`} 
                          className="mobile-team-logo" 
                        />
                      </div>
                    )}
                    <div className="mobile-team-info">
                      <span className="mobile-team-name" style={{ color: color }}>{t.team} Squad</span>
                    </div>
                    <div className="mobile-team-points-wrapper">
                      <span className="mobile-team-points-val">{t.totalPoints}</span>
                      <span className="mobile-team-points-lbl">PTS</span>
                    </div>
                    <div className="mobile-team-expand-icon" style={{ color: color }}>
                      {isExpanded ? '▲' : '▼'}
                    </div>
                  </div>

                  {/* Card Expanded Content Drawer */}
                  {isExpanded && (
                    <div className="mobile-team-card-details">
                      <div className="mobile-details-grid">
                        <div className="mobile-detail-col stats-col">
                          <div className="mobile-detail-item">
                            <span className="mobile-detail-label">Best Event</span>
                            <span className="mobile-detail-val">{t.bestEvent}</span>
                          </div>
                          <div className="mobile-detail-item">
                            <span className="mobile-detail-label">Avg Points / Event</span>
                            <span className="mobile-detail-val">{t.avgPointsPerEvent} pts</span>
                          </div>
                        </div>
                        <div className="mobile-detail-col contributors-col">
                          <span className="mobile-detail-label contributors-header">Top Contributors</span>
                          <div className="contributors-list">
                            {t.topPlayers.map((player, pIdx) => (
                              <div 
                                key={player.name} 
                                className="contributor-row" 
                                onClick={() => openPlayerCard(player.name)}
                              >
                                <span className="contributor-rank" style={{ backgroundColor: `${color}33`, color: color }}>{pIdx + 1}</span>
                                <span className="contributor-name">{player.name}</span>
                                <span className="contributor-pts">{player.points} pts</span>
                              </div>
                            ))}
                            {t.topPlayers.length === 0 && (
                              <span className="no-contributors">No players scored points yet.</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="standings-desktop-main">
            
            {/* Left Column: Line Graph */}
            <div className="standings-graph-container">
              <h2 className="graph-title">Team Progression</h2>
              {(() => {
                const maxEv = Math.max(1, maxEvent);
                const teamsList = ['Blue', 'Green', 'Gold', 'Pink'];
                
                // Find the maximum points for scaling
                let maxVal = 100;
                teamsList.forEach(t => {
                  for (let ev = 1; ev <= maxEv; ev++) {
                    const val = chartData.cumulative[t][ev] || 0;
                    if (val > maxVal) maxVal = val;
                  }
                });
                // Round maxVal to a clean ceiling (e.g. nearest 500)
                const yMaxLimit = Math.ceil(maxVal / 500) * 500 || 500;
                
                // Graph margins/coordinates
                const width = 600;
                const height = 350;
                const paddingLeft = 60;
                const paddingRight = 20;
                const paddingTop = 20;
                const paddingBottom = 40;
                
                const chartW = width - paddingLeft - paddingRight;
                const chartH = height - paddingTop - paddingBottom;
                
                // Helper function to map (event, score) to SVG coords
                const getX = (evNum) => {
                  if (maxEv <= 1) return paddingLeft + chartW / 2;
                  return paddingLeft + ((evNum - 1) / (maxEv - 1)) * chartW;
                };
                
                const getY = (scoreVal) => {
                  return (paddingTop + chartH) - (scoreVal / yMaxLimit) * chartH;
                };

                // Generate points paths
                return (
                  <svg viewBox={`0 0 ${width} ${height}`} className="standings-svg-graph" width="100%" height="100%">
                    <defs>
                      <filter id="neon-glow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="6" result="blur" />
                        <feMerge>
                          <feMergeNode in="blur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>

                    {/* Gridlines */}
                    {[0, 1, 2, 3, 4].map(idx => {
                      const scoreVal = (yMaxLimit / 4) * idx;
                      const y = getY(scoreVal);
                      return (
                        <g key={idx}>
                          <line 
                            x1={paddingLeft} 
                            y1={y} 
                            x2={width - paddingRight} 
                            y2={y} 
                            stroke="rgba(255, 255, 255, 0.08)" 
                            strokeDasharray="4 4" 
                          />
                          <text 
                            x={paddingLeft - 10} 
                            y={y + 4} 
                            fill="rgba(255, 255, 255, 0.5)" 
                            fontSize="11" 
                            textAnchor="end"
                          >
                            {scoreVal}
                          </text>
                        </g>
                      );
                    })}

                    {/* X Axis labels */}
                    {Array.from({ length: maxEv }).map((_, i) => {
                      const evNum = i + 1;
                      const x = getX(evNum);
                      return (
                        <text 
                          key={evNum} 
                          x={x} 
                          y={height - paddingBottom + 25} 
                          fill="rgba(255, 255, 255, 0.5)" 
                          fontSize="12" 
                          textAnchor="middle"
                        >
                          Ev {evNum}
                        </text>
                      );
                    })}

                    {/* Team Lines */}
                    {teamsList.map(team => {
                      const color = SQUAD_COLORS[team.toLowerCase()];
                      const points = [];
                      for (let ev = 1; ev <= maxEv; ev++) {
                        const val = chartData.cumulative[team][ev] || 0;
                        points.push(`${getX(ev)},${getY(val)}`);
                      }
                      
                      const pathD = `M ${points.join(' L ')}`;
                      const isHighlighted = highlightedTeam === team;
                      const isDimmed = highlightedTeam && highlightedTeam !== team;
                      
                      return (
                        <g 
                          key={team}
                          onMouseEnter={() => setHighlightedTeam(team)}
                          onMouseLeave={() => setHighlightedTeam(null)}
                          style={{ cursor: 'pointer' }}
                        >
                          <path 
                            d={pathD}
                            fill="none"
                            stroke={color}
                            strokeWidth={isHighlighted ? 4.5 : 2.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            opacity={isDimmed ? 0.15 : 1}
                            filter={isHighlighted ? 'url(#neon-glow)' : 'none'}
                            style={{ transition: 'stroke-width 0.2s, opacity 0.2s, filter 0.2s' }}
                          />
                          
                          {/* Data points */}
                          {points.map((pt, ptIdx) => {
                            const [x, y] = pt.split(',');
                            const val = chartData.cumulative[team][ptIdx + 1] || 0;
                            return (
                              <circle 
                                key={ptIdx}
                                cx={x}
                                cy={y}
                                r={isHighlighted ? 6 : 4}
                                fill="#0c0c14"
                                stroke={color}
                                strokeWidth={isHighlighted ? 3 : 2}
                                opacity={isDimmed ? 0.15 : 1}
                                style={{ transition: 'r 0.2s, stroke-width 0.2s, opacity 0.2s' }}
                              >
                                <title>{`${team} Squad: ${val} pts (Event ${ptIdx + 1})`}</title>
                              </circle>
                            );
                          })}
                        </g>
                      );
                    })}
                  </svg>
                );
              })()}
            </div>

            {/* Right Column: Sidebar containing Team Standings (75% height) and Stats Card (25% height) */}
            <div className="standings-sidebar-container">
              
              {/* Top: Team Standings List */}
              <div className="standings-teams-list">
                {mobileTeamStandings.map((t, idx) => {
                  const teamLower = t.team.toLowerCase();
                  const logo = TEAM_LOGOS[teamLower];
                  const color = SQUAD_COLORS[teamLower];
                  const isHighlighted = highlightedTeam === t.team;
                  const isDimmed = highlightedTeam && highlightedTeam !== t.team;
                  const isExpanded = !!expandedTeams[t.team];

                  return (
                    <div 
                      key={t.team} 
                      className={`mobile-team-card desktop-team-row-card ${isHighlighted ? 'highlighted' : ''} ${isExpanded ? 'expanded' : ''}`}
                      style={{ 
                        borderLeft: `6px solid ${color}`,
                        opacity: isDimmed ? 0.4 : 1,
                        transition: 'transform 0.2s, opacity 0.2s, box-shadow 0.2s, border-color 0.2s',
                        boxShadow: isHighlighted ? `0 0 15px ${color}55` : undefined,
                        borderColor: isHighlighted ? color : undefined
                      }}
                      onMouseEnter={() => setHighlightedTeam(t.team)}
                      onMouseLeave={() => setHighlightedTeam(null)}
                    >
                      {/* Card Header (clickable to expand) */}
                      <div className="mobile-team-card-header" onClick={() => toggleTeamExpand(t.team)}>
                        <div className="mobile-team-rank">{idx + 1}</div>
                        {logo && (
                          <div className="mobile-team-logo-container">
                            <img 
                              src={logo} 
                              alt={`${t.team} logo`} 
                              className="mobile-team-logo" 
                            />
                          </div>
                        )}
                        <div className="mobile-team-info">
                          <span className="mobile-team-name" style={{ color: color }}>{t.team} Squad</span>
                        </div>
                        <div className="mobile-team-points-wrapper">
                          <span className="mobile-team-points-val">{t.totalPoints}</span>
                          <span className="mobile-team-points-lbl">PTS</span>
                        </div>
                        <div className="mobile-team-expand-icon" style={{ color: color }}>
                          {isExpanded ? '▲' : '▼'}
                        </div>
                      </div>

                      {/* Card Expanded Content Drawer */}
                      {isExpanded && (
                        <div className="mobile-team-card-details">
                          <div className="mobile-details-grid">
                            <div className="mobile-detail-col stats-col">
                              <div className="mobile-detail-item">
                                <span className="mobile-detail-label">Best Event</span>
                                <span className="mobile-detail-val">{t.bestEvent}</span>
                              </div>
                              <div className="mobile-detail-item">
                                <span className="mobile-detail-label">Avg Points / Event</span>
                                <span className="mobile-detail-val">{t.avgPointsPerEvent} pts</span>
                              </div>
                            </div>
                            <div className="mobile-detail-col contributors-col">
                              <span className="mobile-detail-label contributors-header">Top Contributors</span>
                              <div className="contributors-list">
                                {t.topPlayers.map((player, pIdx) => (
                                  <div 
                                    key={player.name} 
                                    className="contributor-row" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openPlayerCard(player.name);
                                    }}
                                  >
                                    <span className="contributor-rank" style={{ backgroundColor: `${color}33`, color: color }}>{pIdx + 1}</span>
                                    <span className="contributor-name">{player.name}</span>
                                    <span className="contributor-pts">{player.points} pts</span>
                                  </div>
                                ))}
                                {t.topPlayers.length === 0 && (
                                  <span className="no-contributors">No players scored points yet.</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>

          </div>
        )}
      </div>

      {/* 5. Player Detail View Modal */}
      {selectedPlayer && (
        <div className="player-modal-backdrop" onClick={() => setSelectedPlayer(null)}>
          <div className="player-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="player-modal-close" onClick={() => setSelectedPlayer(null)}>×</button>
            
            {/* Modal Header */}
            <div className="modal-header-section">
              <h2 className="modal-player-name">{selectedPlayer.name}</h2>
              <div className="modal-subtitle-row">
                <span 
                  className="team-pill" 
                  style={{ 
                    borderColor: SQUAD_COLORS[selectedPlayer.team.toLowerCase()], 
                    color: SQUAD_COLORS[selectedPlayer.team.toLowerCase()],
                    fontSize: '1rem',
                    padding: '0.4rem 1rem'
                  }}
                >
                  {selectedPlayer.team} Squad
                </span>
                <span className="modal-rank-badge">True Rank: #{selectedPlayer.overallRank}</span>
              </div>
            </div>

            {/* Modal Stats Grid */}
            <div className="modal-stats-grid">
              <div className="modal-stat-card">
                <span className="modal-stat-label">Total Points</span>
                <span className="modal-stat-val">{selectedPlayer.totalPoints}</span>
              </div>
              <div className="modal-stat-card">
                <span className="modal-stat-label">Events Played</span>
                <span className="modal-stat-val">{selectedPlayer.eventsPlayed}</span>
              </div>
              <div className="modal-stat-card">
                <span className="modal-stat-label">Games Played</span>
                <span className="modal-stat-val">{selectedPlayer.gamesPlayed}</span>
              </div>
              <div className="modal-stat-card">
                <span className="modal-stat-label">Points Per Game</span>
                <span className="modal-stat-val">{selectedPlayer.ppg.toFixed(2)}</span>
              </div>
            </div>

            {/* Middle Section: Halves & Rooms */}
            <div className="modal-split-grid">
              {/* Halves Breakdown */}
              <div className="modal-sub-section">
                <h3>Half Breakdown</h3>
                <div className="halves-bars-container">
                  <div className="half-bar-row">
                    <span className="half-name">1st Half</span>
                    <span className="half-points">{selectedPlayer.firstHalfPoints} pts</span>
                  </div>
                  <div className="half-progress-bg">
                    <div 
                      className="half-progress-fill first-half-fill" 
                      style={{ 
                        width: `${selectedPlayer.totalPoints > 0 ? (selectedPlayer.firstHalfPoints / selectedPlayer.totalPoints) * 100 : 0}%` 
                      }}
                    ></div>
                  </div>

                  <div className="half-bar-row" style={{ marginTop: '1.2rem' }}>
                    <span className="half-name">2nd Half</span>
                    <span className="half-points">{selectedPlayer.secondHalfPoints} pts</span>
                  </div>
                  <div className="half-progress-bg">
                    <div 
                      className="half-progress-fill second-half-fill" 
                      style={{ 
                        width: `${selectedPlayer.totalPoints > 0 ? (selectedPlayer.secondHalfPoints / selectedPlayer.totalPoints) * 100 : 0}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Room History */}
              <div className="modal-sub-section">
                <h3>Room / Team History</h3>
                <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)', marginBottom: '1rem' }}>
                  Games played inside each colored room:
                </p>
                <div className="room-history-row">
                  {Object.entries(selectedPlayer.roomCounts).map(([color, count]) => (
                    <div 
                      key={color} 
                      className="room-count-card"
                      style={{ borderTop: `4px solid ${SQUAD_COLORS[color.toLowerCase()]}` }}
                    >
                      <span className="room-color-name" style={{ color: SQUAD_COLORS[color.toLowerCase()] }}>{color}</span>
                      <span className="room-games-count">{count} {count === 1 ? 'Game' : 'Games'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Section: Event-by-Event List */}
            <div className="modal-sub-section" style={{ marginTop: '1.5rem' }}>
              <h3>Event-by-Event Performance</h3>
              <table className="modal-event-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Rooms Played In</th>
                    <th>Points</th>
                    <th>Games Played</th>
                    <th>PPG</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPlayer.eventBreakdown.map(eb => (
                    <tr key={eb.event}>
                      <td>Event {eb.event}</td>
                      <td>
                        {eb.rooms.split(', ').map(rm => (
                          <span 
                            key={rm} 
                            className="room-indicator-dot"
                            style={{ backgroundColor: SQUAD_COLORS[rm.toLowerCase()] }}
                            title={rm}
                          ></span>
                        ))}
                        <span style={{ fontSize: '0.85rem', marginLeft: '0.5rem', color: 'rgba(255,255,255,0.7)' }}>
                          {eb.rooms}
                        </span>
                      </td>
                      <td style={{ color: '#2dcfdf', fontWeight: 'bold' }}>{eb.points} pts</td>
                      <td>{eb.games}</td>
                      <td>{eb.ppg.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 6. Squad Player Card Modal (Top Contributors) */}
      {selectedPlayerCard && (
        <div className="player-modal-backdrop" onClick={() => setSelectedPlayerCard(null)}>
          <div className="player-modal-content squad-card-modal-wrapper" onClick={(e) => e.stopPropagation()}>
            <button className="player-modal-close" onClick={() => setSelectedPlayerCard(null)}>×</button>
            
            <div className="squad-card-modal-inner">
              {(() => {
                const p = selectedPlayerCard;
                const isCap = Number(p.Captain) === 1;
                const activeColor = SQUAD_COLORS[p.Team?.toLowerCase()] || '#2dcfdf';
                return (
                  <div
                    className={`player-card ${isCap ? 'captain' : ''}`}
                    style={{
                      borderColor: activeColor,
                      boxShadow: isCap ? `0 0 25px ${activeColor}70` : `0 0 15px ${activeColor}40`,
                      background: isCap ? `radial-gradient(120% 120% at 50% 10%, ${activeColor}22 0%, rgba(10, 10, 15, 0.85) 75%)` : undefined,
                      margin: '0 auto',
                      width: '100%',
                      maxWidth: '320px'
                    }}
                  >
                    {isCap ? (
                      <div className="player-card-header-wrapper">
                        <span 
                          className="captain-badge" 
                          style={{ 
                            color: activeColor,
                            textShadow: `0 0 10px ${activeColor}`
                          }}
                        >
                          ★ CAPTAIN ★
                        </span>
                        <div className="player-card-header">
                          <h3 className="player-card-name">{p.Player}</h3>
                          <span className="player-card-rank">
                            {p.Rank ? `#${p.Rank}` : 'Unranked'}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="player-card-header">
                        <h3 className="player-card-name">{p.Player}</h3>
                        <span className="player-card-rank">
                          {p.Rank ? `#${p.Rank}` : 'Unranked'}
                        </span>
                      </div>
                    )}
                    
                    <div className="player-card-stats">
                      <div className="player-card-stat">
                        <span className="player-card-label">Events</span>
                        <span className="player-card-value">{p["Events Played"] || 0}</span>
                      </div>
                      <div className="player-card-stat">
                        <span className="player-card-label">Games</span>
                        <span className="player-card-value">{p["Games Played"] || 0}</span>
                      </div>
                      <div className="player-card-stat">
                        <span className="player-card-label">Game Pts</span>
                        <span className="player-card-value">{p["Game Points"] || 0}</span>
                      </div>
                      <div className="player-card-stat">
                        <span className="player-card-label">Bonus Pts</span>
                        <span className="player-card-value">{p["Bonus Points"] || 0}</span>
                      </div>
                      <div className="player-card-stat">
                        <span className="player-card-label">Overall</span>
                        <span className="player-card-value">{p["Overall Points"] || 0}</span>
                      </div>
                      <div className="player-card-stat">
                        <span className="player-card-label">Pts/Game</span>
                        <span className="player-card-value">
                          {typeof p["Pts/Game"] === 'number' ? p["Pts/Game"].toFixed(2) : "0.00"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <SocialLinks />
    </div>
  );
}
